import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ChangeEvent, FC, MouseEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import InfoCard from 'components/InfoCard/InfoCard';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { StorageE } from 'declarations/enums/storage';
import type { RoofTopI } from 'declarations/interfaces/roofTop';
import type { DealerWipSummaryItemI, DealerWipTotalsI } from 'declarations/interfaces/wip';
import { useFetchWipDealerSummary } from 'hooks/api/useWip';
import CardLoader from 'pages/TicketsPage/components/CardLoader/CardLoader';
import { CardLoaderWrapStyled } from 'pages/TicketsPage/components/CardLoader/CardLoaderWrapStyled';
import { ReportCardWrapStyled } from 'pages/TicketsPage/TicketsPage.styled';
import {
  resetWipPageNumber,
  selectWipSelectedRoofTopId,
  setSelectedWipRoofTopId,
} from 'reduxStore/features/wip/wipSlice';
import { useAppDispatch, useAppSelector } from 'reduxStore/hooks';

import { DealerTableWrapStyled, SortIconWrapStyled } from './DealerCard.styled';

type SortColumnT = 'claimTotal' | 'dealer' | 'openWip' | 'paidTotal' | 'roTotal';
type SortOrderT = 'asc' | 'desc';

const formatAmount = (amount: number): string =>
  `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatCount = (count: number): string =>
  count.toLocaleString('en-US', { maximumFractionDigits: 0 });

const DealerCard: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { userProfile } = useUserProfile();
  const selectedRoofTopId = useAppSelector(selectWipSelectedRoofTopId);
  const { data, isLoading } = useFetchWipDealerSummary();

  const { isMobile, isSmallMobile } = useResponsive();

  const [sortColumn, setSortColumn] = useState<SortColumnT>('dealer');
  const [sortOrder, setSortOrder] = useState<SortOrderT>('asc');
  const [accordionExpanded, setAccordionExpanded] = useState<boolean>((): boolean => {
    const stored = localStorage.getItem(StorageE.WipDealerCardExpanded);
    return stored !== null ? stored === 'true' : true;
  });

  const selectedDealerRowRef = useRef<HTMLTableRowElement | null>(null);

  useEffect((): void => {
    if (selectedRoofTopId) {
      if (isMobile && !accordionExpanded) {
        setAccordionExpanded(true);
        localStorage.setItem(StorageE.WipDealerCardExpanded, 'true');
      }
      const row = selectedDealerRowRef.current;
      if (row) {
        requestAnimationFrame((): void => {
          row.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }, [selectedRoofTopId, isMobile, accordionExpanded]);

  const mappedRooftops: RoofTopI[] = useMemo(
    (): RoofTopI[] => userProfile?.mappedRoofTops ?? [],
    [userProfile?.mappedRoofTops]
  );

  const dealerRows: DealerWipSummaryItemI[] = useMemo(
    (): DealerWipSummaryItemI[] =>
      (data?.dealers ?? []).map(
        (d): DealerWipSummaryItemI => ({
          roofTopId: String(d.roofTopId),
          roofTopName: d.roofTopName,
          oemFamilyId: String(d.oemFamilyId ?? ''),
          oemFamilyName: d.oemFamilyName ?? '',
          openWipCount: Number(d.openWipCount),
          roTotal: Number(d.roTotal),
          claimTotal: Number(d.claimTotal),
          paidTotal: Number(d.paidTotal),
        })
      ),
    [data?.dealers]
  );

  // The dealer-refine list combines mapped rooftops (the user's Chirp access)
  // with the WIP-authorized dealer set already applied server-side via dealerRows.
  const dealerRefineList: RoofTopI[] = useMemo((): RoofTopI[] => {
    const summaryIds = new Set(dealerRows.map((r): string => r.roofTopId));
    const fromSummary: RoofTopI[] = mappedRooftops.filter((r): boolean => summaryIds.has(r.id));
    return fromSummary.length > 0 ? fromSummary : mappedRooftops;
  }, [mappedRooftops, dealerRows]);

  // --- Dealer refinement dropdown state ---
  const [dealerSelectedIds, setDealerSelectedIds] = useState<string[] | null>(null);
  const [dealerRefineMenuAnchor, setDealerRefineMenuAnchor] = useState<HTMLElement | null>(null);
  const [pendingDealerIds, setPendingDealerIds] = useState<string[] | null>(null);
  const [dealerRefineSearchQuery, setDealerRefineSearchQuery] = useState('');
  const dealerRefineMenuOpen = Boolean(dealerRefineMenuAnchor);

  const handleDealerRefineMenuOpen = (event: MouseEvent<HTMLButtonElement>): void => {
    setPendingDealerIds(
      dealerSelectedIds && dealerSelectedIds.length > 0 ? [...dealerSelectedIds] : null
    );
    setDealerRefineSearchQuery('');
    setDealerRefineMenuAnchor(event.currentTarget);
  };

  const handleDealerRefineMenuClose = (): void => {
    setDealerRefineMenuAnchor(null);
    setDealerRefineSearchQuery('');
  };

  const dealerRefineFilteredList = useMemo((): RoofTopI[] => {
    const list = dealerRefineList;
    const q = dealerRefineSearchQuery.trim().toLowerCase();
    if (q) return list.filter((r): boolean => (r.name ?? '').toLowerCase().includes(q));
    return list;
  }, [dealerRefineList, dealerRefineSearchQuery]);

  const handlePendingToggleAll = (): void => {
    const visibleIds = dealerRefineFilteredList.map((r): string => r.id);
    const allSelected =
      visibleIds.length > 0 &&
      pendingDealerIds !== null &&
      visibleIds.every((id): boolean => pendingDealerIds.includes(id));
    if (allSelected) {
      setPendingDealerIds(null);
    } else {
      setPendingDealerIds(visibleIds.length > 0 ? visibleIds : null);
    }
  };

  const handlePendingToggleDealer = (roofId: string): void => {
    setPendingDealerIds((prev): string[] | null => {
      if (prev === null) return [roofId];
      const next = prev.includes(roofId)
        ? prev.filter((id): boolean => id !== roofId)
        : [...prev, roofId];
      return next.length === 0 ? null : next;
    });
  };

  const handleDealerRefineApply = (): void => {
    setDealerSelectedIds(pendingDealerIds);
    setDealerRefineMenuAnchor(null);
  };

  const handleDealerRefineReset = (): void => {
    setDealerSelectedIds(null);
    setPendingDealerIds(null);
    setDealerRefineMenuAnchor(null);
  };

  const dealerRefineCount = dealerSelectedIds?.length ?? 0;

  const isAllDealersInListSelected = useMemo((): boolean => {
    const visibleIds = dealerRefineFilteredList.map((r): string => r.id);
    if (visibleIds.length === 0) return pendingDealerIds === null;
    return (
      pendingDealerIds !== null && visibleIds.every((id): boolean => pendingDealerIds.includes(id))
    );
  }, [dealerRefineFilteredList, pendingDealerIds]);
  // --- End dealer refinement dropdown state ---

  const handleAccordionChange = (_event: React.SyntheticEvent, expanded: boolean): void => {
    setAccordionExpanded(expanded);
    localStorage.setItem(StorageE.WipDealerCardExpanded, String(expanded));
  };

  const handleSort = (column: SortColumnT): void => {
    if (sortColumn === column) {
      setSortOrder((prev: SortOrderT): SortOrderT => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const handleRowClick = (roofTopId: string): void => {
    dispatch(resetWipPageNumber());
    if (selectedRoofTopId === roofTopId) {
      dispatch(setSelectedWipRoofTopId(null));
    } else {
      dispatch(setSelectedWipRoofTopId(roofTopId));
    }
  };

  const handleAllClick = (): void => {
    dispatch(resetWipPageNumber());
    dispatch(setSelectedWipRoofTopId(null));
  };

  const filteredDealers = useMemo((): DealerWipSummaryItemI[] => {
    if (!dealerSelectedIds || dealerSelectedIds.length === 0) return dealerRows;
    const idSet = new Set(dealerSelectedIds);
    return dealerRows.filter((d): boolean => idSet.has(d.roofTopId));
  }, [dealerRows, dealerSelectedIds]);

  const sortedRooftops = useMemo((): DealerWipSummaryItemI[] => {
    return [...filteredDealers].sort(
      (a: DealerWipSummaryItemI, b: DealerWipSummaryItemI): number => {
        let comparison = 0;
        switch (sortColumn) {
          case 'dealer':
            comparison = a.roofTopName.localeCompare(b.roofTopName);
            break;
          case 'openWip':
            comparison = a.openWipCount - b.openWipCount;
            break;
          case 'roTotal':
            comparison = a.roTotal - b.roTotal;
            break;
          case 'claimTotal':
            comparison = a.claimTotal - b.claimTotal;
            break;
          case 'paidTotal':
            comparison = a.paidTotal - b.paidTotal;
            break;
          default:
            comparison = 0;
        }
        return sortOrder === 'asc' ? comparison : -comparison;
      }
    );
  }, [filteredDealers, sortColumn, sortOrder]);

  const displayTotals = useMemo((): DealerWipTotalsI => {
    if (!data?.totals) {
      return { openWipCount: 0, roTotal: 0, claimTotal: 0, paidTotal: 0 };
    }
    if (!dealerSelectedIds || dealerSelectedIds.length === 0) {
      const t = data.totals;
      return {
        openWipCount: Number(t.openWipCount),
        roTotal: Number(t.roTotal),
        claimTotal: Number(t.claimTotal),
        paidTotal: Number(t.paidTotal),
      };
    }
    return filteredDealers.reduce(
      (acc: DealerWipTotalsI, d: DealerWipSummaryItemI): DealerWipTotalsI => ({
        openWipCount: acc.openWipCount + d.openWipCount,
        roTotal: acc.roTotal + d.roTotal,
        claimTotal: acc.claimTotal + d.claimTotal,
        paidTotal: acc.paidTotal + d.paidTotal,
      }),
      { openWipCount: 0, roTotal: 0, claimTotal: 0, paidTotal: 0 }
    );
  }, [data, dealerSelectedIds, filteredDealers]);

  const dealerCount = sortedRooftops.length;

  const renderSortIcon = (column: SortColumnT): JSX.Element | null => {
    if (sortColumn !== column) return null;

    return (
      <SortIconWrapStyled>
        {sortOrder === 'asc' ? (
          <ArrowUpwardIcon sx={{ fontSize: 14 }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 14 }} />
        )}
      </SortIconWrapStyled>
    );
  };

  const renderCountCell = (count: number, bold: boolean): JSX.Element => (
    <Box
      component="span"
      sx={{
        fontWeight: bold ? 600 : 'inherit',
      }}
    >
      {formatCount(count)}
    </Box>
  );

  const dealerRefineDropdown = (
    <>
      <Button
        id="wip-dealer-refine-button"
        aria-haspopup="listbox"
        aria-expanded={dealerRefineMenuOpen}
        onClick={handleDealerRefineMenuOpen}
        variant="outlined"
        size="small"
        sx={{
          minWidth: 120,
          justifyContent: 'space-between',
          fontSize: 12,
          height: 28,
          textTransform: 'none',
        }}
        endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
      >
        Dealers
        {dealerRefineCount > 0 ? (
          <>
            {' '}
            <Box component="span" sx={{ color: 'success.main', fontWeight: 500 }}>
              {`(${dealerRefineCount})`}
            </Box>
          </>
        ) : null}
      </Button>
      <Menu
        anchorEl={dealerRefineMenuAnchor}
        open={Boolean(dealerRefineMenuAnchor)}
        onClose={handleDealerRefineMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            maxHeight: 380,
            width: 280,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        MenuListProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            p: 0,
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ flexShrink: 0, p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            size="small"
            placeholder="Search dealers"
            value={dealerRefineSearchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>): void =>
              setDealerRefineSearchQuery(e.target.value)
            }
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: dealerRefineSearchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear dealer search"
                    size="small"
                    onClick={(): void => setDealerRefineSearchQuery('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
        <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <ListItemButton onClick={handlePendingToggleAll}>
            <ListItemIcon>
              <Checkbox checked={isAllDealersInListSelected} edge="start" disableRipple />
            </ListItemIcon>
            <ListItemText primary="All" />
          </ListItemButton>
          {dealerRefineFilteredList.map(
            (roof): JSX.Element => (
              <ListItemButton
                key={roof.id}
                onClick={(): void => handlePendingToggleDealer(roof.id)}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={pendingDealerIds !== null && pendingDealerIds.includes(roof.id)}
                    edge="start"
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={roof.name} />
              </ListItemButton>
            )
          )}
          {dealerRefineSearchQuery.trim() && dealerRefineFilteredList.length === 0 && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', px: 2, py: 1 }}>
              No dealers match your search.
            </Typography>
          )}
        </List>
        <Box
          sx={{
            flexShrink: 0,
            p: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            display: 'flex',
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleDealerRefineReset}
            sx={{ flex: 1 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleDealerRefineApply}
            sx={{ flex: 1 }}
          >
            Apply
          </Button>
        </Box>
      </Menu>
      <Tooltip
        title="Select dealers to refine the list"
        arrow
        placement="top"
        slotProps={{
          popper: {
            modifiers: [{ name: 'flip', enabled: true }],
            sx: { zIndex: 1300 },
          },
        }}
      >
        <span style={{ display: 'inline-flex', lineHeight: 0 }}>
          <InfoOutlinedIcon
            sx={{ fontSize: 18, color: 'action.active', cursor: 'help' }}
            aria-label="Select dealers to refine the list"
          />
        </span>
      </Tooltip>
    </>
  );

  const showNumericColumns = !isSmallMobile;
  const isAllSelected = !selectedRoofTopId;

  const renderTable = (): JSX.Element => (
    <DealerTableWrapStyled>
      <Table size="small" stickyHeader>
        {!isSmallMobile && (
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'nowrap',
                    minWidth: 0,
                    '& > *': { flexShrink: 0 },
                  }}
                >
                  <Box
                    component="span"
                    onClick={(): void => handleSort('dealer')}
                    sx={{
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontWeight: 700,
                    }}
                  >
                    Dealers{renderSortIcon('dealer')}
                  </Box>
                  {!isSmallMobile && dealerRefineDropdown}
                </Box>
              </TableCell>
              {showNumericColumns && (
                <>
                  <TableCell align="right" onClick={(): void => handleSort('openWip')}>
                    <Tooltip title="Open WIP">
                      <span>Open WIP</span>
                    </Tooltip>
                    {renderSortIcon('openWip')}
                  </TableCell>
                  <TableCell align="right" onClick={(): void => handleSort('roTotal')}>
                    <Tooltip title="RO Total">
                      <span>RO Total</span>
                    </Tooltip>
                    {renderSortIcon('roTotal')}
                  </TableCell>
                  <TableCell align="right" onClick={(): void => handleSort('claimTotal')}>
                    <Tooltip title="Claim Total">
                      <span>Claim</span>
                    </Tooltip>
                    {renderSortIcon('claimTotal')}
                  </TableCell>
                  <TableCell align="right" onClick={(): void => handleSort('paidTotal')}>
                    <Tooltip title="Paid Total">
                      <span>Paid</span>
                    </Tooltip>
                    {renderSortIcon('paidTotal')}
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          <TableRow
            hover
            selected={isAllSelected}
            onClick={handleAllClick}
            sx={{ cursor: 'pointer' }}
          >
            <TableCell>
              <Box component="span" sx={{ fontWeight: 600 }}>
                All
              </Box>
            </TableCell>
            {showNumericColumns && (
              <>
                <TableCell align="right">
                  {renderCountCell(displayTotals.openWipCount, true)}
                </TableCell>
                <TableCell align="right">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {formatAmount(displayTotals.roTotal)}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {formatAmount(displayTotals.claimTotal)}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {formatAmount(displayTotals.paidTotal)}
                  </Box>
                </TableCell>
              </>
            )}
          </TableRow>

          {sortedRooftops.map((rooftop: DealerWipSummaryItemI): JSX.Element => {
            const isSelected = selectedRoofTopId === rooftop.roofTopId;

            return (
              <TableRow
                key={rooftop.roofTopId}
                ref={isSelected ? selectedDealerRowRef : undefined}
                hover
                selected={isSelected}
                onClick={(): void => handleRowClick(rooftop.roofTopId)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Tooltip title={rooftop.roofTopName} enterDelay={500}>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        maxWidth: 160,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {rooftop.roofTopName}
                    </Box>
                  </Tooltip>
                </TableCell>
                {showNumericColumns && (
                  <>
                    <TableCell align="right">
                      {renderCountCell(rooftop.openWipCount, false)}
                    </TableCell>
                    <TableCell align="right">{formatAmount(rooftop.roTotal)}</TableCell>
                    <TableCell align="right">{formatAmount(rooftop.claimTotal)}</TableCell>
                    <TableCell align="right">{formatAmount(rooftop.paidTotal)}</TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </DealerTableWrapStyled>
  );

  if (isLoading) {
    if (isMobile) {
      return (
        <Accordion expanded={accordionExpanded} onChange={handleAccordionChange}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Dealers</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <CardLoaderWrapStyled>
              <CardLoader />
            </CardLoaderWrapStyled>
          </AccordionDetails>
        </Accordion>
      );
    }

    return (
      <ReportCardWrapStyled>
        <InfoCard>
          <CardLoaderWrapStyled>
            <CardLoader />
          </CardLoaderWrapStyled>
        </InfoCard>
      </ReportCardWrapStyled>
    );
  }

  if (isMobile) {
    return (
      <Accordion expanded={accordionExpanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">
            Dealers
            {!isSmallMobile && ` (${dealerCount})`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0, overflow: 'auto', maxHeight: '50vh' }}>
          {renderTable()}
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <ReportCardWrapStyled>
      <InfoCard>{renderTable()}</InfoCard>
    </ReportCardWrapStyled>
  );
};

export default DealerCard;
